// "Set status" in the account menu (and the mobile profile sheet): a custom message others see on your
// profile card, with an emoji and an optional time it clears itself. Clicking the field slides an editor
// open below it — recent statuses, a few presets, when to clear, Cancel / Save — instead of a dialog.
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import clsx from 'clsx';
import { endOfDay, endOfWeek } from 'date-fns';
import { ChevronDown, CornerDownLeft, SmilePlus, X } from '@/components/ui/icons';
import { useDb, useMe, mutate, patch } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { useUi, pushStatusRecent, type StatusChoice, type StatusClear } from '@/lib/ui';
import { statusUntilLabel, userStatus } from '@/lib/format';
import { Popover } from '@/components/ui/Popover';
import { Tooltip } from '@/components/ui/Tooltip';
import { Menu, MenuItem, MenuLabel } from '@/components/ui/Menu';

const CLEAR: { key: StatusClear; label: string; suffix: string }[] = [
  { key: 'none', label: "Don't clear", suffix: '' },
  { key: '30m', label: '30 minutes', suffix: 'for 30 minutes' },
  { key: '1h', label: '1 hour', suffix: 'for an hour' },
  { key: '2h', label: '2 hours', suffix: 'for 2 hours' },
  { key: '4h', label: '4 hours', suffix: 'for 4 hours' },
  { key: 'today', label: 'Today', suffix: 'for today' },
  { key: 'week', label: 'This week', suffix: 'for this week' },
];

const PRESETS: StatusChoice[] = [
  { emoji: '📅', text: 'In a meeting', clear: '1h' },
  { emoji: '🎧', text: 'Focusing', clear: '2h' },
  { emoji: '🚌', text: 'Commuting', clear: '30m' },
  { emoji: '🤒', text: 'Out sick', clear: 'today' },
  { emoji: '🌴', text: 'On vacation', clear: 'none' },
];

const EMOJIS = ['🙂', '😎', '🤓', '🥳', '🤔', '😴', '🤒', '🤕', '👋', '🙌', '💪', '🧠', '🎯', '🎧', '💻', '📅', '📞', '🎤', '📚', '✍️', '🛠️', '🚀', '🔥', '✅', '⏳', '🔕', '☕', '🍔', '🏠', '🏢', '🚌', '🚗', '✈️', '🌴', '🏖️', '🏃', '🧘', '👶', '🌙', '🎉'];

function untilFor(clear: StatusClear, now = new Date()): number | null {
  switch (clear) {
    case '30m':
      return now.getTime() + 30 * 60_000;
    case '1h':
      return now.getTime() + 60 * 60_000;
    case '2h':
      return now.getTime() + 2 * 60 * 60_000;
    case '4h':
      return now.getTime() + 4 * 60 * 60_000;
    case 'today':
      return endOfDay(now).getTime();
    case 'week':
      return endOfWeek(now, { weekStartsOn: 1 }).getTime();
    default:
      return null;
  }
}

/** "📅 In a meeting" → emoji + text (a status saved without an emoji keeps it all as text). */
function splitStatus(s: string): { emoji: string; text: string } {
  const first = new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(s)[Symbol.iterator]().next().value?.segment ?? '';
  return first && /\p{Extended_Pictographic}/u.test(first) ? { emoji: first, text: s.slice(first.length).trimStart() } : { emoji: '', text: s };
}

/**
 * Someone's status beside their name in a list: its emoji, or the text, faint. Hovering shows the status
 * and when it clears.
 */
export function StatusBadge({ userId }: { userId: string }) {
  const status = useDb((db) => userStatus(db.users[userId]));
  const until = useDb((db) => db.users[userId]?.statusUntil ?? null);
  if (!status) return null;
  const { emoji, text } = splitStatus(status);
  const tip = (
    <span className="status-tip">
      <span className="status-tip-main">{status}</span>
      {until && <span className="status-tip-until">{statusUntilLabel(until)}</span>}
    </span>
  );
  return (
    <Tooltip label={tip}>
      {emoji ? <span className="status-badge">{emoji}</span> : <span className="status-badge-text truncate">{text}</span>}
    </Tooltip>
  );
}

/** `inert` for React 18, which passes the attribute through but doesn't type it */
const inert = (on: boolean) => (on ? ({ inert: '' } as object) : {});

export function StatusField({ onEditingChange, panelMinHeight }: { onEditingChange?: (editing: boolean) => void; panelMinHeight?: number }) {
  const me = useMe();
  const ws = useWorkspaceId();
  const user = useDb((db) => db.users[me]);
  const wsName = useDb((db) => db.workspaces[ws]?.name ?? 'your workspace');
  const recents = useUi((s) => s.statusRecents);
  const current = userStatus(user);
  const shown = splitStatus(current);
  const [editing, setEditingState] = useState(false);
  const [emoji, setEmoji] = useState('');
  const [text, setText] = useState('');
  // 'keep': leave the current status's clear time as it is
  const [clear, setClear] = useState<StatusClear | 'keep'>('none');
  const [initial, setInitial] = useState({ value: '', clear: 'none' as StatusClear | 'keep' });
  const inputRef = useRef<HTMLInputElement>(null);

  const setEditing = (on: boolean) => {
    setEditingState(on);
    onEditingChange?.(on);
  };
  const focusInput = () => requestAnimationFrame(() => inputRef.current?.focus());
  const start = () => {
    const keep = current && user?.statusUntil ? 'keep' : 'none';
    setEmoji(shown.emoji);
    setText(shown.text);
    setClear(keep);
    setInitial({ value: current, clear: keep });
    setEditing(true);
    focusInput();
  };
  const value = [emoji, text.trim()].filter(Boolean).join(' ').slice(0, 100);
  const dirty = value !== initial.value || clear !== initial.clear;
  const save = () => {
    const status = value || null;
    const statusUntil = !status ? null : clear === 'keep' ? (user?.statusUntil ?? null) : untilFor(clear);
    if (status !== (user?.status ?? null) || statusUntil !== (user?.statusUntil ?? null)) mutate([patch('users', me, { status, statusUntil })]);
    if (status && text.trim()) pushStatusRecent({ emoji, text: text.trim(), clear: clear === 'keep' ? 'none' : clear });
    setEditing(false);
  };
  const clearNow = () => mutate([patch('users', me, { status: null, statusUntil: null })]);
  const pick = (c: StatusChoice) => {
    setEmoji(c.emoji);
    setText(c.text);
    setClear(c.clear);
    focusInput();
  };

  // the editor may unmount mid-edit (the menu closes): tell the menu it's back to normal
  useEffect(() => () => onEditingChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearLabel = clear === 'keep' ? (user?.statusUntil ? statusUntilLabel(user.statusUntil) : "Don't clear") : CLEAR.find((c) => c.key === clear)!.label;
  const row = (c: StatusChoice, key: string) => {
    const suffix = CLEAR.find((x) => x.key === c.clear)?.suffix;
    return (
      <button key={key} type="button" className="menu-item acct-status-choice" onClick={() => pick(c)}>
        <span className="acct-status-choice-emoji">{c.emoji}</span>
        <span className="truncate">
          {c.text}
          {suffix && <span className="acct-status-choice-when"> — {suffix}</span>}
        </span>
      </button>
    );
  };

  return (
    <div
      className={clsx('acct-status-wrap', { editing })}
      onKeyDown={(e) => {
        // Escape cancels the edit and keeps the menu open (keys from the emoji / clear-after popovers close only those)
        if (!editing || e.key !== 'Escape' || !e.currentTarget.contains(e.target as Node)) return;
        e.stopPropagation();
        setEditing(false);
      }}
    >
      {editing ? (
        <div className="acct-status editing">
          <Popover
            placement="bottom-start"
            content={(close) => (
              <div className="acct-emoji">
                <div className="acct-emoji-grid">
                  {EMOJIS.map((e) => (
                    <button key={e} type="button" onClick={() => (setEmoji(e), close(), focusInput())}>
                      {e}
                    </button>
                  ))}
                </div>
                {emoji && (
                  <button type="button" className="menu-item acct-emoji-none" onClick={() => (setEmoji(''), close(), focusInput())}>
                    No emoji
                  </button>
                )}
              </div>
            )}
          >
            <button type="button" className="acct-status-emoji" aria-label="Choose an emoji">
              {emoji || <SmilePlus size={16} />}
            </button>
          </Popover>
          <input
            ref={inputRef}
            value={text}
            maxLength={100}
            placeholder="What's on your mind?"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && dirty && save()}
          />
          {(text || emoji) && (
            <span className="acct-status-clear" role="button" aria-label="Clear" onClick={() => (setText(''), setEmoji(''), setClear('none'), focusInput())}>
              <X size={13} />
            </span>
          )}
        </div>
      ) : (
        <div className="acct-status" role="button" tabIndex={0} onClick={start} onKeyDown={(e) => e.key === 'Enter' && start()}>
          <span className="acct-status-emoji">{shown.emoji || <SmilePlus size={16} />}</span>
          <span className={current ? 'grow truncate' : 'grow truncate faint'}>{current ? shown.text : 'Set status'}</span>
          {current && (
            <Tooltip label="Clear status">
              <span
                className="acct-status-clear"
                role="button"
                aria-label="Clear status"
                onClick={(e) => {
                  e.stopPropagation();
                  clearNow();
                }}
              >
                <X size={13} />
              </span>
            </Tooltip>
          )}
        </div>
      )}
      {/* slides open below the field (grid rows 0fr → 1fr) */}
      <div className={clsx('acct-status-panel', { open: editing })} aria-hidden={!editing} {...inert(!editing)}>
        <div className="acct-status-panel-clip">
          <div className="acct-status-panel-inner" style={{ minHeight: panelMinHeight } as CSSProperties}>
            <div className="acct-status-when">
              <span>Clear after</span>
              <Popover
                placement="bottom-end"
                content={(close) => (
                  <Menu style={{ width: 180 }}>
                    {CLEAR.map((c) => (
                      <MenuItem key={c.key} label={c.label} checked={clear === c.key} onClick={() => (setClear(c.key), close())} />
                    ))}
                  </Menu>
                )}
              >
                <button type="button" className="acct-status-when-btn">
                  {clearLabel} <ChevronDown size={14} />
                </button>
              </Popover>
            </div>
            {recents.length > 0 && (
              <>
                <MenuLabel>Recents</MenuLabel>
                {recents.map((c, i) => row(c, `r${i}`))}
              </>
            )}
            <MenuLabel>For {wsName}</MenuLabel>
            {PRESETS.map((c) => row(c, c.text))}
            <div className="acct-status-foot">
              <button type="button" className="btn sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button type="button" className="btn sm primary" disabled={!dirty} onClick={save}>
                Save <CornerDownLeft size={13} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
